import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, matchPath, useLocation, useNavigate, useParams } from 'react-router';
import classNames from 'clsx';
import qs from 'query-string';

import { AVAILABLE_CHIP_TAGS, SYSTEM_TAG_IAB } from '../../constants/label';
import {
  QUERY_SEARCH_KEY,
  QUERY_SELECTED_CHIP_IAB_KEY,
  QUERY_SELECTED_CHIP_KEY,
  QUERY_SELECTED_CHIP_TAG_KEY,
} from '../../constants/queryParams';
import { ALL_EXISTING_ROUTES } from '../../constants/routePath';
import { MAIN_PLAYER_INSTANCE_KEY } from '../Player/constants';
import { SIZE_SMALL } from '@/mui/constants';

import { VideoType } from '@/modules/common/types/video';

import {
  arrayDifference,
  findCaseInsensitive,
  getIntersection,
  getUniqueStringsWithRepeats,
  includesCaseInsensitive,
} from '../../helpers/array';
import { processHistoryObject } from '../../helpers/history';
import logger from '../../helpers/logger';
import { safetyJsonParse } from '../../helpers/request';
import * as commonSelectors from '../../redux/selectors/common';
import { usePlayerWidget } from '../Player/helpers';
import { useTranslate } from '@/modules/common/helpers/i18n';

import ChipsSkeleton from '@/modules/common/components/Chips/ChipsSkeleton';
import { Chip, Tab, Tabs } from '@/mui/components';

import styles from './Chips.module.scss';

const CHIP_ALL_LABEL = 'All';
const TYPE_ORDER = AVAILABLE_CHIP_TAGS.reduce<Record<string | number, number>>(
  (obj, type, index) => ({ ...obj, [type]: index }),
  {},
);

const sortEntitiesByTypeAndValue = (a: VideoType['videoEntities'][number], b: VideoType['videoEntities'][number]) => {
  if (TYPE_ORDER[a.type] === TYPE_ORDER[b.type]) {
    return a.value.localeCompare(b.value);
  }
  return TYPE_ORDER[a.type] - TYPE_ORDER[b.type];
};

type Props = {
  videos: VideoType[];
  className?: string;
  loading?: boolean;
};

function Chips({ videos, className, loading = false }: Props) {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const t = useTranslate();
  const watchId = useSelector(commonSelectors.idSelector);
  const fixedTags = useSelector(commonSelectors.fixedTagsSelector);
  const excludedTags = useSelector(commonSelectors.excludedTagsSelector);
  const tagCount = useSelector(commonSelectors.tagCountSelector);
  const tagMin = useSelector(commonSelectors.tagMinSelector);
  const tagMax = useSelector(commonSelectors.tagMaxSelector);
  const configuration = useSelector(commonSelectors.configurationSelector);
  const LWP = usePlayerWidget(MAIN_PLAYER_INSTANCE_KEY);

  const chipsConfig = configuration.common.chips;

  const excludedTagsLowerCase = excludedTags.map((chip) => chip.toLowerCase());

  const currentRoute = ALL_EXISTING_ROUTES.find((route) => matchPath(route, location.pathname));
  const queryChipObj = qs.parse(location.search)[QUERY_SELECTED_CHIP_KEY] as string;
  const { [QUERY_SELECTED_CHIP_TAG_KEY]: queryChip = '', [QUERY_SELECTED_CHIP_IAB_KEY]: queryChipIabIds } =
    safetyJsonParse(queryChipObj);

  const [referredTag, setReferredTag] = useState(queryChip);

  const videoEntities = videos.reduce<VideoType['videoEntities']>((acc, video) => {
    video.videoEntities.forEach((videoEntity) => {
      if (!excludedTagsLowerCase.includes(videoEntity.value.toLowerCase())) {
        acc.push(videoEntity);
      }
    });

    return acc;
  }, []);

  const iabEntities = videoEntities.reduce<Record<string, (string | undefined)[]>>((acc, videoEntity) => {
    if (videoEntity.type === SYSTEM_TAG_IAB) {
      const key = videoEntity.value.toLowerCase();

      if (!acc[key]) {
        acc[key] = [videoEntity.id];
      } else if (!acc[key].includes(videoEntity.id)) {
        acc[key].push(videoEntity.id);
      }
    }
    return acc;
  }, {});

  const getQueryWithTag = (tag: string): Record<string, string> => {
    const result: Record<string, string> = {
      ...(qs.parse(location.search) as object),
    };

    if (tag) {
      result[QUERY_SELECTED_CHIP_KEY] = JSON.stringify({
        [QUERY_SELECTED_CHIP_TAG_KEY]: tag,
        [QUERY_SELECTED_CHIP_IAB_KEY]: iabEntities[tag.toLowerCase()] || undefined,
      });
    } else {
      delete result[QUERY_SELECTED_CHIP_KEY];
    }

    return result;
  };

  const getChipUrl = (tag: string) => {
    const query = qs.stringify(getQueryWithTag(tag));
    const queryPrefix = query ? `?${query}` : '';

    return `${location.pathname}${queryPrefix}`;
  };

  let chipsToShow: string[] = [];
  let videoTags: string[] = [];

  if (videos.length >= tagCount || queryChip || referredTag) {
    videoTags = videos
      .reduce<VideoType['videoEntities']>((acc, video) => {
        const sortedVideoEntities = video.videoEntities
          .slice()
          .filter((videoEntity) => !excludedTagsLowerCase.includes(videoEntity.value.toLowerCase()))
          .sort(sortEntitiesByTypeAndValue);

        const uniqueValues: Record<string, boolean | undefined> = {};
        const uniqueVideoEntities: VideoType['videoEntities'] = [];

        sortedVideoEntities.forEach((obj) => {
          if (!uniqueValues[obj.value.toLowerCase()]) {
            uniqueValues[obj.value.toLowerCase()] = true;
            uniqueVideoEntities.push(obj);
          }
        });

        return acc.concat(uniqueVideoEntities);
      }, [])
      .sort(sortEntitiesByTypeAndValue)
      .map((entity) => entity.value);

    const availableFixedTags = getIntersection(fixedTags, videoTags);
    const uniqueVideoTags = getUniqueStringsWithRepeats(videoTags, tagCount);
    const uniqueExceptFixedTags = arrayDifference(uniqueVideoTags, availableFixedTags);

    chipsToShow = availableFixedTags.concat(uniqueExceptFixedTags).slice(0, tagMax);

    if (referredTag && !includesCaseInsensitive(chipsToShow, referredTag)) {
      chipsToShow.splice(0, 0, referredTag);
      chipsToShow = chipsToShow.slice(0, tagMax);
    }
  }

  const chipsWithTypes = chipsToShow.reduce<Record<string, string[]>>((acc, chip) => {
    acc[chip] = [
      ...new Set(
        videoEntities
          .filter((videoEntity) => videoEntity.value.toLowerCase() === chip.toLowerCase())
          .map((videoEntity) => videoEntity.type),
      ),
    ];
    return acc;
  }, {});

  logger.log(
    'CHIPS_LOGGING',
    '\nfixedTags -',
    fixedTags,
    '\nexcludedTags -',
    excludedTags,
    '\nqueryChip -',
    queryChip,
    '\nqueryChipIabIds -',
    queryChipIabIds,
    '\nreferredTag -',
    referredTag,
    '\nchipsToShow -',
    chipsToShow,
    '\nchipsWithTypes -',
    chipsWithTypes,
    '\niabEntities -',
    iabEntities,
  );

  useEffect(() => {
    setReferredTag(queryChip);
  }, [currentRoute]);

  useEffect(() => {
    if (queryChip) {
      const queryChipFromChipsToShow = findCaseInsensitive(chipsToShow, queryChip);
      let queryChipFromVideoTags;
      if (!queryChipFromChipsToShow) {
        queryChipFromVideoTags = findCaseInsensitive(videoTags, queryChip);
      }

      if (chipsToShow.length && chipsToShow.length >= tagMin && queryChipFromChipsToShow) {
        // removed from redux state
        // dispatch(setSelectedChipAction(queryChipFromChipsToShow));
      } else if (videoTags.length && queryChipFromVideoTags) {
        if (!referredTag) {
          setReferredTag(queryChipFromVideoTags);
        }
      } else if (videos.length) {
        setReferredTag(null);
        navigate(
          processHistoryObject({
            search: getQueryWithTag(''),
          }),
          { replace: true },
        );
      }
    }
  }, [videos.map(({ videoId }) => videoId).join('|'), queryChip, queryChipIabIds]);

  const placeholder = loading ? (
    <div className={classNames(styles.Wrapper, className)}>
      <ChipsSkeleton />
    </div>
  ) : null;

  return !loading && (chipsToShow.length >= tagMin || referredTag) ? (
    <div className={classNames(styles.Wrapper, className)}>
      <Tabs
        size="small"
        variant="scrollable"
        value={queryChip.toLowerCase()}
        allowScrollButtonsMobile
        classes={{
          root: styles.TabsRoot,
          list: styles.TabsList,
          indicator: styles.TabsIndicator,
        }}
        aria-label={t.videoCompilationsBySubject.text()}
      >
        {[''].concat(chipsToShow).map((tag, index) => (
          <Tab
            key={tag}
            label={
              <Chip
                style={{ fontFamily: chipsConfig.fontFamily }}
                label={!index ? t.allChips.text() : tag}
                to={getChipUrl(tag)}
                component={RouterLink}
                size={chipsConfig.size ?? SIZE_SMALL}
                color={
                  queryChip.toLowerCase() === tag.toLowerCase()
                    ? (chipsConfig.selectedColor ?? 'primary')
                    : (chipsConfig.color ?? 'primary')
                }
                variant={
                  queryChip.toLowerCase() === tag.toLowerCase()
                    ? (chipsConfig.selectedVariant ?? 'filled')
                    : (chipsConfig.variant ?? 'outlined')
                }
                className={styles.Chip}
                onClick={() => {
                  LWP.sendStatsChipClicked(
                    watchId,
                    currentRoute,
                    params.channel,
                    qs.parse(location.search)[QUERY_SEARCH_KEY],
                    !index ? CHIP_ALL_LABEL : tag,
                    index,
                    chipsWithTypes[tag]?.join('|'),
                  );
                }}
              />
            }
            value={tag.toLowerCase()}
            disableRipple
            className={styles.Tab}
          />
        ))}
      </Tabs>
    </div>
  ) : (
    placeholder
  );
}

export default Chips;
